(() => {
  const name = 'dwan-gallery-position';
  if (customElements.get(name)) return;

  customElements.define(name, class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const info = this.closest('product-info');
      const main = info?.closest('main#MainContent');
      const gallery = this.closest('media-gallery');
      const excluded = info?.closest('quick-add-modal, quick-add-bulk, product-modal, modal-dialog, [role="dialog"]');
      const primary = main?.querySelector('product-info[id^="MainProduct-"][data-update-url="true"]');
      if (!info || excluded || info !== primary || info.getAttribute('data-update-url') !== 'true'
          || !gallery || gallery !== info.querySelector('media-gallery') || gallery.id !== this.dataset.galleryId) {
        this.remove();
        return;
      }
      this.gallery = gallery;
      this.label = this.querySelector('[data-gallery-position-label]');
      this.fill = this.querySelector('[data-gallery-position-fill]');
      if (!this.label || !this.fill) return;
      this.initialized = true;
      this.activeId = null;
      this.pendingTarget = null;
      this.viewerId = null;
      this.lastThumbId = undefined;
      this.lastActiveId = undefined;
      this.frame = undefined;
      this.queue = () => {
        if (this.frame !== undefined) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = undefined;
          this.update();
        });
      };
      this.onSlide = (event) => {
        const viewer = this.gallery.querySelector('[id^="GalleryViewer-"]');
        if (event.target !== viewer) return; // Thumbnail scrolling is not a media selection.
        const current = event.detail?.currentElement;
        if (!current || !this.items().includes(current)) return;
        const id = current.dataset.mediaId;
        this.viewerId = id;
        // Smooth native scrolling may report every intermediate slide. A thumbnail
        // click represents its destination immediately, not those traversed slides.
        if (this.pendingTarget && this.pendingTarget.id !== id) return;
        this.pendingTarget = null; // Destination reached; no timer or lasting selection lock.
        this.snapshot();
        this.activeId = id;
        this.queue();
      };
      this.onClick = (event) => {
        const button = event.target?.closest?.('button');
        const item = button?.closest('[data-target]');
        const rail = item?.closest('[id^="GalleryThumbnails-"]');
        if (!rail || !this.gallery.contains(rail) || button.disabled) return;
        const items = this.items();
        const ids = items.map((media) => media.dataset.mediaId);
        if (!ids.includes(item.dataset.target)) return;
        this.snapshot(); // Native button handler has already updated the gallery.
        const list = this.gallery.querySelector('[id^="Slider-Gallery-"]');
        const media = items.find((entry) => entry.dataset.mediaId === item.dataset.target);
        const atTarget = this.viewerId === item.dataset.target
          || (list && media && Math.abs(list.scrollLeft - media.offsetLeft) < 0.5);
        this.pendingTarget = atTarget ? null
          : { id: item.dataset.target, order: ids.join('|'), active: this.mediaId(), list };
        this.activeId = item.dataset.target;
        this.update();
      };
      this.onManual = (event) => {
        const viewer = this.gallery.querySelector('[id^="GalleryViewer-"]');
        if (!viewer?.contains(event.target) || !this.pendingTarget) return;
        this.pendingTarget = null;
        this.snapshot();
        if (this.items().some((item) => item.dataset.mediaId === this.viewerId)) this.activeId = this.viewerId;
        this.queue();
      };
      this.onScrollEnd = (event) => {
        const list = this.gallery.querySelector('[id^="Slider-Gallery-"]');
        if (event.target !== list || !this.pendingTarget) return;
        this.pendingTarget = null;
        this.snapshot();
        if (this.items().some((item) => item.dataset.mediaId === this.viewerId)) this.activeId = this.viewerId;
        this.queue();
      };
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.queue();
      });
      this.observer.observe(this.gallery, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'hidden', 'style', 'aria-current'],
      });
      // Native slideChanged does not bubble. Capture only events from the main viewer.
      this.gallery.addEventListener('slideChanged', this.onSlide, true);
      this.gallery.addEventListener('click', this.onClick);
      this.gallery.addEventListener('scrollend', this.onScrollEnd, true);
      for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
        this.gallery.addEventListener(type, this.onManual, true);
      }
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.cancelAnimationFrame(this.frame);
      this.frame = undefined;
      this.gallery?.removeEventListener('slideChanged', this.onSlide, true);
      this.gallery?.removeEventListener('click', this.onClick);
      this.gallery?.removeEventListener('scrollend', this.onScrollEnd, true);
      for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
        this.gallery?.removeEventListener(type, this.onManual, true);
      }
      this.pendingTarget = null;
      this.initialized = false;
    }

    items() {
      const list = this.gallery.querySelector('[id^="Slider-Gallery-"]');
      if (!list) return [];
      const seen = new Set();
      // The shared Liquid emits only eligible main media. Inactive slides may be
      // display:none because of the gallery layout and must still count.
      return Array.from(list.children).filter((item) => {
        const id = item.dataset?.mediaId;
        if (!item.matches('li[data-media-id]') || !id || seen.has(id) || item.hidden
            || item.classList.contains('hidden') || item.style.display === 'none') return false;
        seen.add(id);
        return true;
      });
    }

    thumbId() {
      return this.gallery.querySelector('[id^="GalleryThumbnails-"] button[aria-current="true"]')
        ?.closest('[data-target]')?.dataset.target || null;
    }

    mediaId() {
      return this.gallery.querySelector('[id^="Slider-Gallery-"] > li[data-media-id].is-active')
        ?.dataset.mediaId || null;
    }

    snapshot() {
      this.lastThumbId = this.thumbId();
      this.lastActiveId = this.mediaId();
    }

    update() {
      if (!this.initialized) return;
      const items = this.items();
      const ids = items.map((item) => item.dataset.mediaId);
      const thumb = this.thumbId();
      const active = this.mediaId();
      if (this.pendingTarget) {
        const changed = this.pendingTarget.list !== this.gallery.querySelector('[id^="Slider-Gallery-"]')
          || this.pendingTarget.order !== ids.join('|')
          || active !== this.pendingTarget.active;
        if (changed || !ids.includes(this.pendingTarget.id)) {
          this.pendingTarget = null;
          this.activeId = ids.includes(active) ? active : null;
          this.snapshot();
        } else this.activeId = this.pendingTarget.id;
      }
      // A viewer event remains authoritative until native DOM selection changes.
      // This avoids reverting to stale .is-active while native aria-current is debounced.
      if (this.pendingTarget) this.activeId = this.pendingTarget.id;
      else if (thumb && thumb !== this.lastThumbId && ids.includes(thumb)) this.activeId = thumb;
      else if (active && active !== this.lastActiveId && ids.includes(active)) this.activeId = active;
      else if (!ids.includes(this.activeId)) {
        this.activeId = ids.includes(thumb) ? thumb : ids.includes(active) ? active : null;
      }
      this.lastThumbId = thumb;
      this.lastActiveId = active;
      const index = ids.indexOf(this.activeId);
      const visible = items.length > 1 && index >= 0;
      if (this.hidden !== !visible) this.hidden = !visible;
      if (!visible) return;
      const text = `Image ${index + 1} of ${items.length}`;
      if (this.label.textContent !== text) this.label.textContent = text;
      const count = String(items.length);
      const ordinal = String(index);
      if (this.fill.style.getPropertyValue('--dwan-gallery-count') !== count) {
        this.fill.style.setProperty('--dwan-gallery-count', count);
      }
      if (this.fill.style.getPropertyValue('--dwan-gallery-index') !== ordinal) {
        this.fill.style.setProperty('--dwan-gallery-index', ordinal);
      }
    }
  });
})();
